import { ForwardRefExoticComponent, RefAttributes, forwardRef, useEffect } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, ButtonBase, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import useConfig from '@/hooks/useConfig';
import MiniNavTooltip from '../MiniNavTooltip';
import { useDispatch, useSelector } from '@/store';
import { activeID, activeItem, openDrawer } from '@/store/slices/menu';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { LinkTarget, NavItemType } from '@/types';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

interface NavItemProps {
    item: NavItemType;
    level: number;
    parentId: string;
}

const NavItem = ({ item, level, parentId }: NavItemProps) => {
    const theme = useTheme();
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { borderRadius } = useConfig();

    const { selectedItem, drawerOpen } = useSelector((state) => state.menu);
    const routeMatchedByUrl = item.url ? Boolean(matchPath({ path: item.url, end: true }, pathname)) : false;
    const isSelected = selectedItem.findIndex((id) => id === item.id) > -1 || routeMatchedByUrl;

    const Icon = item?.icon!;
    const itemIcon = item?.icon ? (
        <Icon
            strokeWidth={1.5}
            sx={{
                color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary
            }}
            size={drawerOpen ? '20px' : '24px'}
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary,
                width: isSelected ? 8 : 6,
                height: isSelected ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget: LinkTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps: {
        component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
        href?: string;
        target?: LinkTarget;
    } = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url!} target={itemTarget} />) };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id: string) => {
        dispatch(activeItem([id]));
        if (matchesSM) dispatch(openDrawer(false));
        dispatch(activeID(parentId));
    };

    // keep active menu state in sync for dynamic routes
    useEffect(() => {
        if (routeMatchedByUrl && item.id) {
            dispatch(activeItem([item.id]));
            dispatch(activeID(parentId));
        }
    }, [dispatch, item.id, parentId, routeMatchedByUrl]);

    const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'text.primary';
    const iconSelectedColor = theme.palette.mode === 'dark' && drawerOpen ? 'text.primary' : 'secondary.main';
    const iconMinWidth = level === 1 ? 36 : item.icon ? 32 : 18;
    const showMiniLabel = !drawerOpen && level === 1;
    const miniAriaLabel = showMiniLabel
        ? [item.title, item.caption].filter((value): value is string => typeof value === 'string' && value.length > 0).join('. ')
        : undefined;
    const captionSx = {
        ...theme.typography.subMenuCaption,
        display: 'block' as const,
        ...(isSelected && { color: theme.palette.text.primary })
    };

    const listItem = (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            disableRipple={!drawerOpen}
            sx={{
                zIndex: 1201,
                borderRadius: `${borderRadius}px`,
                mb: 0.5,
                pl: drawerOpen ? `${level * 24}px` : 1.25,
                ...(drawerOpen &&
                    level === 1 &&
                    theme.palette.mode !== 'dark' && {
                        '&:hover': {
                            background: theme.palette.secondary.light
                        },
                        '&.Mui-selected': {
                            background: theme.palette.secondary.light,
                            color: iconSelectedColor,
                            '&:hover': {
                                color: iconSelectedColor,
                                background: theme.palette.secondary.light
                            }
                        }
                    }),
                ...((!drawerOpen || level !== 1) && {
                    py: level === 1 ? 0 : 1,
                    '&:hover': {
                        bgcolor: 'transparent'
                    },
                    '&.Mui-selected': {
                        '&:hover': {
                            bgcolor: 'transparent'
                        },
                        bgcolor: 'transparent'
                    }
                })
            }}
            selected={isSelected}
            onClick={() => itemHandler(item.id!)}
            aria-label={miniAriaLabel || undefined}
        >
            <ButtonBase sx={{ borderRadius: `${borderRadius}px` }} disableRipple={drawerOpen}>
                <ListItemIcon
                    sx={{
                        minWidth: iconMinWidth,
                        color: isSelected ? iconSelectedColor : textColor,
                        ...(!drawerOpen &&
                            level === 1 && {
                                borderRadius: `${borderRadius}px`,
                                width: 46,
                                height: 46,
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 25 : 'secondary.light'
                                },
                                ...(isSelected && {
                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 25 : 'secondary.light',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 30 : 'secondary.light'
                                    }
                                })
                            })
                    }}
                >
                    {itemIcon}
                </ListItemIcon>
            </ButtonBase>

            {(drawerOpen || (!drawerOpen && level !== 1)) && (
                <ListItemText
                    primary={
                        <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit">
                            {item.title}
                        </Typography>
                    }
                    secondary={
                        item.caption && (
                            <Typography variant="caption" sx={captionSx} gutterBottom>
                                {item.caption}
                            </Typography>
                        )
                    }
                />
            )}

            {drawerOpen && item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );

    if (showMiniLabel) {
        return (
            <MiniNavTooltip title={item.title} caption={item.caption}>
                {listItem}
            </MiniNavTooltip>
        );
    }

    return listItem;
};

export default NavItem;
